import { UserRoundCheck } from 'lucide-react';
import {
  displayName,
  formatDuration,
  formatListTime,
  formatOutcome,
  formatStep,
  formatWindowLeft,
  parseTime,
} from '@/lib/conversationFormat';
import { isRecentlyActive } from '@/lib/liveConversation';
import type { ListRow } from '@/lib/conversationList';
import { stripWhatsAppMarkup } from '@/lib/whatsappMarkup';
import { ElapsedClock, SinceText } from './TimeText';
import { useServerNow } from './useServerNow';
import type { AgentLabel } from './ContextPanel';
import { Avatar, joinParts, RowShell } from './ListRowParts';
import CallbackTicketRow, { type ResolveTicket } from './CallbackTicketRow';

/** "Agent springt in 3:18 ein" — ticks by itself. */
function AgentStepsInText({ deadline }: { deadline: string | null }) {
  const now = useServerNow(1000, parseTime(deadline) !== null);
  const at = parseTime(deadline);
  if (at === null) return null;
  const left = at - now;
  return <>{left > 0 ? `Agent springt in ${formatDuration(left)} ein` : 'Agent springt gleich ein'}</>;
}

function WindowText({ until }: { until: string | null }) {
  const now = useServerNow(60_000, parseTime(until) !== null);
  const at = parseTime(until);
  if (at === null) return null;
  const left = formatWindowLeft(at - now);
  return <>{left ? `Fenster ${left}` : 'Fenster abgelaufen – bitte anrufen'}</>;
}

type RowProps = {
  row: ListRow;
  selected: boolean;
  onSelect: () => void;
  now: number;
  agentLabel?: AgentLabel;
  /** Several agent lines: say which one the customer used. */
  showAgent: boolean;
  /** „Erledigt“ of a Rückruf-Ticket row. */
  onResolveTicket?: ResolveTicket;
};

export function ConversationListRow({ row, selected, onSelect, now, agentLabel, showAgent, onResolveTicket }: RowProps) {
  if (row.kind === 'ticket') {
    return (
      <CallbackTicketRow
        ticket={row.ticket}
        selected={selected}
        onSelect={onSelect}
        onResolve={onResolveTicket}
        agent={showAgent ? agentLabel?.(row.ticket.agent_number) : null}
      />
    );
  }

  if (row.kind === 'waiting') {
    const w = row.waiting;
    const title = displayName(w.customer_name, w.customer_phone);
    const agent = showAgent ? agentLabel?.(w.agent_number) : null;
    if (w.state === 'callback') {
      return (
        <RowShell
          selected={selected}
          onSelect={onSelect}
          avatar={<Avatar name={w.customer_name} phone={w.customer_phone} channel="whatsapp" />}
          title={title}
          accent
          aside={<SinceText since={w.waiting_since} />}
          line={joinParts([w.reason || w.summary, <WindowText until={w.reply_window_until} />, agent])}
        />
      );
    }
    return (
      <RowShell
        selected={selected}
        onSelect={onSelect}
        avatar={<Avatar name={w.customer_name} phone={w.customer_phone} channel="whatsapp" live />}
        title={title}
        accent
        aside={<ElapsedClock since={w.waiting_since} />}
        line={joinParts([
          w.state === 'staff' ? 'Wartet auf Antwort' : w.reason || w.summary || 'Möchte einen Mitarbeiter',
          <AgentStepsInText deadline={w.deadline_at} />,
          agent,
        ])}
      />
    );
  }

  if (row.kind === 'call') {
    const c = row.call;
    const agent = showAgent ? agentLabel?.(c.agent_number) : null;
    return (
      <RowShell
        selected={selected}
        onSelect={onSelect}
        avatar={<Avatar name={c.customer_name} phone={c.customer_phone} channel="phone" live />}
        title={displayName(c.customer_name, c.customer_phone)}
        aside={<ElapsedClock since={c.started_at} />}
        line={joinParts([
          c.status === 'transferring' ? 'Wird weitergeleitet …' : formatStep(c.last_step) ?? 'Anruf läuft',
          agent,
        ])}
      />
    );
  }

  const item = row.item;
  const preview = stripWhatsAppMarkup(item.preview);
  const outcome = item.type === 'phone' ? formatOutcome(item.status) : null;
  const live = item.type === 'whatsapp' && isRecentlyActive(item.last_message_at, now);
  const taken = item.type === 'whatsapp' && (item.takeover_state === 'staff' || item.takeover_active);
  const agent = showAgent ? agentLabel?.(item.agent_number) : null;
  return (
    <RowShell
      selected={selected}
      onSelect={onSelect}
      avatar={<Avatar name={item.customer_name} phone={item.customer_phone} channel={item.type} />}
      title={displayName(item.customer_name, item.customer_phone)}
      badges={
        <>
          {taken && (
            <span
              className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-brand-soft text-brand-strong"
              title="Übernommen – ein Mitarbeiter schreibt"
            >
              <UserRoundCheck className="h-3 w-3" aria-hidden="true" />
              <span className="sr-only">Übernommen</span>
            </span>
          )}
          {item.is_test && (
            <span className="flex-shrink-0 rounded border border-border px-1 text-[10px] font-semibold uppercase text-muted-foreground">
              Test
            </span>
          )}
          {live && (
            <span className="relative flex h-2 w-2 flex-shrink-0" title="In den letzten 10 Minuten aktiv">
              <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
              <span className="sr-only">Live</span>
            </span>
          )}
        </>
      }
      aside={formatListTime(item.last_message_at, now)}
      line={joinParts([outcome, preview || (item.type === 'phone' ? 'Telefonat' : 'WhatsApp-Chat'), agent])}
    />
  );
}

export default ConversationListRow;
